import { useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import useFeatures from "../../hooks/useFeatures";
import api from "../../api";

// Clinical oversight (backend GET /oversight/metrics): headline numbers as
// tiles, then a per-clinician table. Org admins see the practice; supervisors
// their team.
const iso = (d) => d.toISOString().slice(0, 10);
const dash = (v, suffix = "") => (v === null || v === undefined ? "-" : `${v}${suffix}`);

function Tile({ label, value, hint, tone }) {
  return (
    <div className="rounded-xl bg-white border border-secondary-100 p-4">
      <p className="text-xs font-medium text-secondary-600">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tone || "text-secondary-900"}`}>{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-secondary-500">{hint}</p>}
    </div>
  );
}

// One row per questionnaire with patients measured at least twice. Improved /
// worse use a published reliable-change figure; other rows show change only.
function Outcomes({ rows }) {
  return (
    <div className="rounded-xl border border-secondary-100 bg-white">
      <div className="px-4 pt-3 pb-2">
        <h2 className="text-sm font-semibold text-secondary-900">Outcomes by questionnaire</h2>
        <p className="text-xs text-secondary-500">First to latest score, for patients who filled it in at least twice.</p>
      </div>
      {rows.length === 0 ? <p className="px-4 pb-4 text-sm text-secondary-500">No repeated questionnaires yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-left text-xs uppercase tracking-wide text-secondary-600">
              <tr><th className="px-4 py-2">Questionnaire</th><th className="px-4 py-2 text-right">Patients</th><th className="px-4 py-2 text-right">Average change</th><th className="px-4 py-2 text-right">Reliably improved</th><th className="px-4 py-2 text-right">Reliably worse</th></tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {rows.map((r) => {
                const better = r.higher_is_better ? r.avg_change > 0 : r.avg_change < 0;
                return (
                  <tr key={`${r.code}-${r.subscale || ""}`}>
                    <td className="px-4 py-2.5"><span className="font-semibold text-secondary-900">{r.label}</span><span className="ml-2 text-[11px] text-secondary-500">{r.higher_is_better ? "higher is better" : "lower is better"}</span></td>
                    <td className="px-4 py-2.5 text-right">{r.patients}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold ${r.avg_change === 0 ? "" : better ? "text-emerald-700" : "text-rose-700"}`}>{r.avg_change > 0 ? `+${r.avg_change}` : r.avg_change}</td>
                    {r.reliable_change ? (
                      <>
                        <td className="px-4 py-2.5 text-right" title={`a change of ${r.reliable_change} points or more`}>{dash(r.improved_pct, "%")}</td>
                        <td className={`px-4 py-2.5 text-right ${r.worse_pct ? "text-rose-700" : ""}`} title={`a change of ${r.reliable_change} points or more`}>{dash(r.worse_pct, "%")}</td>
                      </>
                    ) : <td colSpan={2} className="px-4 py-2.5 text-right text-xs text-secondary-500">change only</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Oversight() {
  const features = useFeatures();
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getTime() - 29 * 86400000)));
  const [to, setTo] = useState(iso(today));
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.fetchOrgBranches().then((r) => setBranches(r.data || [])).catch(() => setBranches([])); }, []);
  useEffect(() => {
    if (!features.has("oversight")) return;
    setData(null);
    api.getOversight({ from, to, branch_id: branch || undefined }).then((r) => { setData(r.data); setError(""); }).catch((e) => setError(e?.response?.data?.error || "Couldn't load the figures."));
  }, [features.ready, from, to, branch]); // eslint-disable-line
  if (features.ready && !features.has("oversight")) return <DashboardLayout><div className="p-8 text-secondary-600">Clinical oversight isn't switched on for your organisation yet.</div></DashboardLayout>;

  const t = data?.totals;
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-secondary-900"><Gauge className="w-6 h-6 text-primary-600" /> Clinical oversight</h1>
            <p className="text-sm text-secondary-600">{data?.scope === "team" ? "Your team" : "The whole practice"}: quality, risk and outcomes for the period.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="From" className="rounded-lg border border-secondary-300 px-2 py-1.5" />
            <span className="text-secondary-500">to</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="To" className="rounded-lg border border-secondary-300 px-2 py-1.5" />
            {branches.length > 1 && (
              <select value={branch} onChange={(e) => setBranch(e.target.value)} aria-label="Branch" className="rounded-lg border border-secondary-300 bg-white px-2 py-1.5">
                <option value="">All branches</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </div>
        </div>
        {error ? <p className="text-sm text-rose-600">{error}</p> : !data ? <p className="text-sm text-secondary-500">Loading…</p> : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Tile label="Active patients" value={t.active_patients} hint="with an appointment in the period" />
              <Tile label="Sessions held" value={t.sessions} hint="session notes in the period" />
              <Tile label="Notes signed within 24h" value={dash(t.notes_signed_24h_pct, "%")} hint={t.avg_note_turnaround_hours !== null ? `average ${t.avg_note_turnaround_hours} h to sign` : "note signing off or no signed notes"} />
              <Tile label="No-show rate" value={dash(t.no_show_rate_pct, "%")} hint="of past appointments" tone={t.no_show_rate_pct >= 15 ? "text-amber-700" : undefined} />
              <Tile label="High-risk patients" value={t.risk.high} hint={t.risk.high_with_plan_pct !== null ? `${t.risk.high_with_plan_pct}% have a safety plan` : `${t.risk.moderate} moderate`} tone={t.risk.high ? "text-rose-700" : undefined} />
              <Tile label="Moderate risk" value={t.risk.moderate} />
              <Tile label="Open supervision" value={t.open_supervision} />
            </div>
            <Outcomes rows={t.outcomes || []} />
            <div className="overflow-x-auto rounded-xl border border-secondary-100 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-secondary-50 text-left text-xs uppercase tracking-wide text-secondary-600">
                  <tr><th className="px-4 py-3">Clinician</th><th className="px-4 py-3 text-right">Sessions</th><th className="px-4 py-3 text-right">Patients</th><th className="px-4 py-3 text-right">High risk</th><th className="px-4 py-3 text-right">No-shows</th><th className="px-4 py-3 text-right">Signed ≤ 24h</th><th className="px-4 py-3 text-right">Avg to sign</th></tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {data.clinicians.map((c) => (
                    <tr key={c.user_id}>
                      <td className="px-4 py-3 font-semibold text-secondary-900">{c.name}</td>
                      <td className="px-4 py-3 text-right">{c.sessions}</td>
                      <td className="px-4 py-3 text-right">{c.patients}</td>
                      <td className={`px-4 py-3 text-right ${c.high_risk ? "font-bold text-rose-700" : ""}`}>{c.high_risk}</td>
                      <td className="px-4 py-3 text-right">{c.no_shows}{c.no_show_rate_pct !== null ? <span className="text-secondary-500"> ({c.no_show_rate_pct}%)</span> : ""}</td>
                      <td className="px-4 py-3 text-right">{dash(c.notes_signed_24h_pct, "%")}</td>
                      <td className="px-4 py-3 text-right">{dash(c.avg_note_turnaround_hours, " h")}</td>
                    </tr>
                  ))}
                  {data.clinicians.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-500">No clinicians in scope.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
